import React, { createContext, useContext, useState, useRef } from 'react';
import { createOnboardingAnnouncement } from '../services/firebase/announcements';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [isOnboardingMode, setIsOnboardingMode] = useState(false);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState('dashboard');
  const [onboardingProgress, setOnboardingProgress] = useState({
    dashboard: false,
    existingConditions: false,
    nda: false,
    profileCompletion: false,
    tasksUnlocked: false,
    salesAssessment: false,
    salesGoals: false
  });
  const onAnnouncementCreatedRef = useRef(null);
  const [showOnboardingAnnouncement, setShowOnboardingAnnouncement] = useState(false);
  const [hasOnboardingKey, setHasOnboardingKey] = useState(false);
  const [hasUsedOnboardingKey, setHasUsedOnboardingKey] = useState(false);

  const startOnboarding = async (userId) => {
    console.log('Starting onboarding for user:', userId);
    setIsOnboardingMode(true);
    setCurrentOnboardingStep('dashboard');
    setOnboardingProgress({
      dashboard: false,
      existingConditions: false,
      nda: false,
      profileCompletion: false,
      tasksUnlocked: false,
      salesAssessment: false,
      salesGoals: false
    });
    
    // Show onboarding announcement after 3 seconds
    setTimeout(() => {
      console.log('Showing onboarding announcement...');
      setShowOnboardingAnnouncement(true);
      console.log('showOnboardingAnnouncement set to true');
      // Trigger callback to refresh announcements
      if (onAnnouncementCreatedRef.current) {
        console.log('Triggering announcement callback...');
        onAnnouncementCreatedRef.current();
      } else {
        console.log('No announcement callback set');
      }
    }, 3000);
  };

  const exitOnboarding = () => {
    setIsOnboardingMode(false);
    setCurrentOnboardingStep('dashboard');
    setShowOnboardingAnnouncement(false);
  };

  const closeOnboardingAnnouncement = () => {
    setShowOnboardingAnnouncement(false);
  };

  const claimOnboardingKey = () => {
    setHasOnboardingKey(true);
  };

  const useOnboardingKey = () => {
    setHasUsedOnboardingKey(true);
  };

  const goToNextStep = (nextStep) => {
    setCurrentOnboardingStep(nextStep);
    setOnboardingProgress(prev => ({
      ...prev,
      [currentOnboardingStep]: true
    }));
  };

  const markStepComplete = (step) => {
    setOnboardingProgress(prev => ({
      ...prev,
      [step]: true
    }));
  };

  const setAnnouncementCreatedCallback = (callback) => {
    console.log('Setting announcement callback:', callback);
    onAnnouncementCreatedRef.current = callback;
    console.log('Callback ref set to:', onAnnouncementCreatedRef.current);
  };

  const value = {
    isOnboardingMode,
    currentOnboardingStep,
    onboardingProgress,
    showOnboardingAnnouncement,
    hasOnboardingKey,
    hasUsedOnboardingKey,
    startOnboarding,
    exitOnboarding,
    closeOnboardingAnnouncement,
    claimOnboardingKey,
    useOnboardingKey,
    goToNextStep,
    markStepComplete,
    setAnnouncementCreatedCallback,
    setShowOnboardingAnnouncement
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}; 